import React, { useEffect, useMemo } from 'react';
import { FormControl, FormLabel, MenuItem, Select, Stack } from '@mui/material';
import { useTableSort } from '@/context/tableSortContext';
import { useKanbanContext } from '@/context/kanbanContext';
import { useFeatureFlagContext } from '@/context/featureFlagContext';
import { getKanbanSortHeadCells } from '@/components/tables/DealTable/data';

const NONE_VALUE = '';

export const KanbanSortSelect = () => {
	const { pipelineId, clearDealStages } = useKanbanContext();
	const { currentSort, setSortModel, clearSort } = useTableSort();
	const { getFeatureFlags } = useFeatureFlagContext();
	const showNewBusinessKPI = Boolean(getFeatureFlags('newBusinessKPI')?.active);

	const sortFields = useMemo(
		() => getKanbanSortHeadCells({ pipeline: pipelineId, showNewBusinessKPI }),
		[pipelineId, showNewBusinessKPI],
	);

	const fieldValue = currentSort?.field || NONE_VALUE;
	const directionValue = currentSort?.sort || 'asc';

	const sortKey = `${currentSort?.field ?? ''}:${currentSort?.sort ?? ''}`;

	useEffect(() => {
		clearDealStages();
	}, [sortKey, clearDealStages]);

	const handleFieldChange = (event) => {
		const nextField = event.target.value;

		if (!nextField) {
			clearSort();
			return;
		}

		setSortModel([
			{
				field: nextField,
				sort: currentSort?.sort || 'asc',
			},
		]);
	};

	const handleDirectionChange = (event) => {
		const nextSort = event.target.value;

		if (!currentSort?.field) {
			return;
		}

		setSortModel([
			{
				field: currentSort.field,
				sort: nextSort,
			},
		]);
	};

	return (
		<Stack direction='row' alignItems='flex-end' spacing={1}>
			<FormControl size='small' sx={{ minWidth: 160 }}>
				<FormLabel sx={{ fontSize: '0.875rem', fontWeight: 600 }}>Sort by</FormLabel>
				<Select
					value={fieldValue}
					onChange={handleFieldChange}
					displayEmpty
					sx={{ height: 40 }}
				>
					<MenuItem value={NONE_VALUE}>None</MenuItem>
					{sortFields.map((cell) => (
						<MenuItem key={cell.id} value={cell.id}>
							{cell.label}
						</MenuItem>
					))}
				</Select>
			</FormControl>
			<FormControl size='small' sx={{ minWidth: 110 }} disabled={!fieldValue}>
				{/* Spacer matches Sort by label height so both selects share a baseline */}
				<FormLabel sx={{ fontSize: '0.875rem', fontWeight: 600, visibility: 'hidden' }}>
					&nbsp;
				</FormLabel>
				<Select
					value={fieldValue ? directionValue : 'asc'}
					onChange={handleDirectionChange}
					sx={{ height: 40 }}
				>
					<MenuItem value='asc'>Ascending</MenuItem>
					<MenuItem value='desc'>Descending</MenuItem>
				</Select>
			</FormControl>
		</Stack>
	);
};
